<template>
  <b-modal
    id="crew-change-modal"
    v-model="visible"
    :title="form.id ? 'Rediger mannskapsendring' : order ? 'Endre mannskap – ordre ' + order.ordernumber : 'Registrer fravær'"
    size="lg"
    no-close-on-backdrop
  >
    <b-form-group label="Hvem kan ikke delta?" label-class="font-weight-bold">
      <b-form-select v-model="form.crew_out" :options="crewOutOptions" @change="onCrewOutChange" />
      <small class="text-muted">La stå tom for å legge til en ekstra person.</small>
    </b-form-group>

    <b-form-group label="Gjelder" label-class="font-weight-bold">
      <b-form-radio-group v-model="scope" :options="scopeOptions" @change="loadAvailability" />
    </b-form-group>

    <b-form-row v-if="scope === 'periode'">
      <b-col sm="6">
        <b-form-group label="Fra">
          <b-form-input v-model="form.from_date" type="date" @change="loadAvailability" />
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group label="Til">
          <b-form-input v-model="form.to_date" type="date" :min="form.from_date" @change="loadAvailability" />
        </b-form-group>
      </b-col>
    </b-form-row>
    <p v-if="scope === 'periode'" class="small text-muted mt-n2">
      <template v-if="!order && !form.crew_in">Personen tas ut av alle ordrer i perioden.</template>
      <template v-else>
        Gjelder alle ordrer
        <span v-if="contextSkiftName">for {{ contextSkiftName }}</span>
        som overlapper perioden.
      </template>
    </p>

    <b-form-group label="Årsak" label-class="font-weight-bold">
      <b-form-radio-group v-model="form.reason" :options="reasons" buttons button-variant="outline-secondary" size="sm" />
    </b-form-group>

    <b-form-group label-class="font-weight-bold">
      <template #label>
        Erstatter
        <b-spinner v-if="loadingAvailability" small class="ml-2"></b-spinner>
      </template>
      <b-form-input v-model="search" size="sm" placeholder="Søk etter navn eller rolle" class="mb-2" />
      <div class="cp-pick-list">
        <div
          class="cp-pick border rounded p-2 mb-2 d-flex align-items-center"
          :class="{ 'cp-pick-selected': !form.crew_in }"
          @click="form.crew_in = null"
        >
          <i class="ion mr-2" :class="!form.crew_in ? 'ion-ios-checkmark-circle text-success' : 'ion-ios-radio-button-off text-muted'"></i>
          <span class="flex-grow-1">Ingen erstatter</span>
        </div>
        <div
          v-for="c in candidates"
          :key="c.id"
          class="cp-pick border rounded p-2 mb-2 d-flex align-items-center"
          :class="{ 'cp-pick-selected': form.crew_in === c.id }"
          @click="form.crew_in = c.id"
        >
          <i class="ion mr-2" :class="form.crew_in === c.id ? 'ion-ios-checkmark-circle text-success' : 'ion-ios-radio-button-off text-muted'"></i>
          <span class="flex-grow-1">
            {{ c.name }}
            <span class="text-muted small">· {{ c.role || "Ukjent rolle" }}</span>
            <span v-if="c.skift" class="text-muted small">· {{ skiftName(c.skift) }}</span>
          </span>
          <b-badge pill :variant="statusVariant(c.status)" :title="statusTitle(c)">{{ statusLabel(c) }}</b-badge>
        </div>
      </div>
    </b-form-group>

    <b-form-group label="Notat">
      <b-form-textarea v-model="form.note" rows="2" max-rows="4" />
    </b-form-group>

    <b-alert v-if="error" show variant="danger" class="small mb-0">{{ error }}</b-alert>

    <template #modal-footer>
      <div class="w-100">
        <b-button v-if="form.id" variant="outline-danger" class="float-left" :disabled="saving" @click="remove()">Slett</b-button>
        <b-button variant="primary" class="float-right" :disabled="saving || !canSave" @click="save()">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>Lagre
        </b-button>
        <b-button variant="default" class="float-right mr-2" @click="visible = false">Avbryt</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import dayjs from "dayjs";
import api, { REASONS, reasonLabel, errorText } from "./api";

const STATUS = {
  ledig: { text: "Ledig", variant: "success" },
  paa_vakt: { text: "På vakt", variant: "warning" },
  opptatt: { text: "Opptatt", variant: "danger" },
  fravaer: { text: "Fravær", variant: "secondary" },
};
const STATUS_ORDER = ["ledig", "paa_vakt", "opptatt", "fravaer"];

export default {
  name: "CrewChangeModal",
  props: {
    plan: { type: Object, default: null },
  },
  data() {
    return {
      visible: false,
      vessel: null,
      order: null,
      scope: "ordre",
      form: this.emptyForm(),
      availability: [],
      loadingAvailability: false,
      search: "",
      saving: false,
      error: null,
      reasons: REASONS,
    };
  },
  computed: {
    crew() {
      return (this.plan && this.plan.crew) || [];
    },
    crewOutOptions() {
      // With an order: the people on it. Without: everyone.
      const people = this.order ? this.order.crew : this.crew;
      return [{ value: null, text: "Ingen – legg til ekstra person" }].concat(
        people.map((c) => ({ value: c.id, text: c.name + (c.role ? " (" + c.role + ")" : "") }))
      );
    },
    scopeOptions() {
      const options = [{ value: "periode", text: "En periode (alle ordrer i perioden)" }];
      if (this.order) options.unshift({ value: "ordre", text: "Kun ordre " + this.order.ordernumber });
      return options;
    },
    crewOut() {
      return this.crew.find((c) => c.id === this.form.crew_out) || null;
    },
    // The skift a period change is tied to: the order's skift, else the absent person's skift.
    contextSkift() {
      if (this.order && this.order.skift) return this.order.skift;
      return this.crewOut ? this.crewOut.skift : null;
    },
    contextSkiftName() {
      return this.contextSkift ? this.skiftName(this.contextSkift) : "";
    },
    range() {
      if (this.scope === "ordre" && this.order) return { from: this.order.startdate, to: this.order.enddate };
      return { from: this.form.from_date, to: this.form.to_date || this.form.from_date };
    },
    candidates() {
      const onOrder = new Set(this.order ? this.order.crew.map((c) => c.id) : []);
      const role = this.crewOut && this.crewOut.role;
      const q = this.search.trim().toLowerCase();
      const byId = {};
      this.availability.forEach((a) => (byId[a.id] = a));
      return this.crew
        .filter((c) => c.id !== this.form.crew_out && (!onOrder.has(c.id) || c.id === this.form.crew_in))
        .map((c) => ({ ...c, ...(byId[c.id] || { status: "ledig" }) }))
        .filter((c) => !q || (c.name + " " + (c.role || "")).toLowerCase().includes(q))
        .sort((a, b) => {
          const r = (b.role === role) - (a.role === role);
          if (r) return r;
          const s = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
          return s || String(a.name).localeCompare(String(b.name), "nb");
        });
    },
    canSave() {
      if (!this.form.crew_out && !this.form.crew_in) return false;
      if (this.scope === "periode") return !!this.form.from_date && !!this.form.to_date && this.form.from_date <= this.form.to_date;
      return !!this.order;
    },
  },
  methods: {
    emptyForm() {
      return { id: null, crew_out: null, crew_in: null, from_date: null, to_date: null, reason: "syk", note: "" };
    },
    /**
     * open({ vessel, order, change, crewOut })
     * - order + crewOut: replace someone on an order
     * - change: edit an existing change
     * - nothing: register an absence
     */
    open({ vessel = null, order = null, change = null, crewOut = null } = {}) {
      this.vessel = vessel;
      this.order = order;
      this.search = "";
      this.error = null;
      this.availability = [];

      if (change) {
        this.order = change.order ? this.findOrder(change.order) : null;
        this.vessel = change.vessel ? this.findVessel(change.vessel) : null;
        this.form = {
          id: change.id,
          crew_out: change.crew_out ? change.crew_out.id : null,
          crew_in: change.crew_in ? change.crew_in.id : null,
          from_date: change.from_date,
          to_date: change.to_date,
          reason: change.reason || "annet",
          note: change.note || "",
        };
        this.scope = change.order ? "ordre" : "periode";
      } else {
        const today = dayjs().format("YYYY-MM-DD");
        this.form = {
          ...this.emptyForm(),
          crew_out: crewOut ? crewOut.id : null,
          from_date: order ? order.startdate : today,
          to_date: order ? order.enddate : today,
          reason: crewOut ? "syk" : "annet",
        };
        this.scope = order ? "ordre" : "periode";
      }
      this.visible = true;
      this.loadAvailability();
    },
    findOrder(id) {
      for (const v of (this.plan && this.plan.vessels) || []) {
        const o = v.orders.find((x) => x.id === id);
        if (o) return o;
      }
      return null;
    },
    findVessel(id) {
      return ((this.plan && this.plan.vessels) || []).find((v) => v.id === id) || null;
    },
    onCrewOutChange() {
      if (!this.order && this.form.crew_out && !this.form.id) this.form.reason = "syk";
    },
    async loadAvailability() {
      const { from, to } = this.range;
      if (!from) return;
      this.loadingAvailability = true;
      try {
        this.availability = await api.getAvailability({ from, to, order: this.order ? this.order.id : undefined });
      } catch (error) {
        console.log(error);
        this.availability = [];
      } finally {
        this.loadingAvailability = false;
      }
    },
    skiftName(id) {
      const s = ((this.plan && this.plan.skifts) || []).find((x) => x.id === id);
      return s ? s.name : "";
    },
    statusLabel(c) {
      return (STATUS[c.status] || STATUS.ledig).text;
    },
    statusVariant(status) {
      return (STATUS[status] || STATUS.ledig).variant;
    },
    statusTitle(c) {
      if (c.status === "opptatt") return "På ordre " + (c.ordernumber || "") + (c.vessel_name ? " (" + c.vessel_name + ")" : "");
      if (c.status === "fravaer") return reasonLabel(c.reason) + " " + (c.from || "") + " – " + (c.to || "");
      if (c.status === "paa_vakt") return "Eget skift er på vakt i perioden";
      return "Ledig i perioden";
    },
    payload() {
      const byOrder = this.scope === "ordre" && this.order;
      // A plain absence (no order, no replacement) follows the person to every order they are on.
      const anywhere = !this.order && !this.form.crew_in;
      const vesselId = (this.vessel && this.vessel.id) || (this.crewOut && this.crewOut.vessel) || null;
      return {
        id: this.form.id || undefined,
        order: byOrder ? this.order.id : null,
        from_date: byOrder ? null : this.form.from_date,
        to_date: byOrder ? null : this.form.to_date,
        vessel: anywhere ? null : vesselId,
        skift: anywhere ? null : this.contextSkift || null,
        crew_out: this.form.crew_out || null,
        crew_in: this.form.crew_in || null,
        reason: this.form.reason,
        note: this.form.note || null,
      };
    },
    async save() {
      this.saving = true;
      this.error = null;
      try {
        await api.saveChange(this.payload());
        this.visible = false;
        this.$bvToast.toast("Mannskapet er oppdatert på berørte ordrer.", { title: "Lagret", variant: "success", solid: true, autoHideDelay: 2500 });
        this.$emit("saved");
      } catch (error) {
        console.log(error);
        this.error = errorText(error);
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      const ok = await this.$bvModal.msgBoxConfirm("Slette endringen? Mannskapet regnes ut på nytt for berørte ordrer.", {
        title: "Slett endring",
        okTitle: "Slett",
        cancelTitle: "Avbryt",
        okVariant: "danger",
      });
      if (!ok) return;
      this.saving = true;
      try {
        await api.deleteChange(this.form.id);
        this.visible = false;
        this.$emit("saved");
      } catch (error) {
        this.error = errorText(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="css">
.cp-pick-list {
  max-height: 280px;
  overflow-y: auto;
}
.cp-pick {
  cursor: pointer;
  background-color: #f8f9fa;
  transition: background-color 0.15s, border-color 0.15s;
}
.cp-pick:hover {
  background-color: #eef1f4;
}
.cp-pick-selected {
  background-color: #eaf6ec;
  border-color: #28a745 !important;
}
</style>
